import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField, TimeField } from '@/components/fields';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Star, Bell, Mail, Phone, Send, Building2, Briefcase, Plus, X,
  CalendarClock, MapPin, Clock, Trash2, Loader2, Upload, FileText, Check, Sparkles, Lock, UserPlus, Pencil,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { tagColor } from '@/lib/contactColors';
import { contactHref, contactLinkProps } from '@/lib/contactLinks';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useAuth } from '@/contexts/AuthContext';
import { minDateStr, wallTimeToUtc } from '@/lib/datetime';
import { classifyLink } from '@/lib/linkUtils';

function fmtDateTime(iso, locale) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(locale, {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
function fmtFollowUp(fu, tz, locale) {
  if (fu?.due_at_utc) {
    try {
      return new Date(fu.due_at_utc).toLocaleString(locale, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: tz,
      });
    } catch { /* fall through */ }
  }
  // Legacy date-only reminder — show the date without a misleading time.
  const d = fu?.due_date;
  if (!d) return '—';
  try {
    return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(locale, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}
function fmtActivityDateTime(activity, locale) {
  const iso = activity?.due_at_utc;
  if (!iso) return activity?.local_due_text || '—';
  try {
    const options = {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    };
    if (activity.timezone) options.timeZone = activity.timezone;
    const value = new Date(iso).toLocaleString(locale, options);
    return activity.timezone ? `${value} (${activity.timezone})` : value;
  } catch {
    return activity?.local_due_text || fmtDateTime(iso, locale);
  }
}


const TYPE_ICON = {
  booking: CalendarClock, quick_invite: CalendarClock, queue: Clock,
  property: MapPin, poll: CalendarClock,
};

export function ContactDetailDrawer({ contactId, suggestedTags, allTags, onClose, onPatched, onDeleted, onPlan, onActivitiesChanged }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('ru') ? 'ru-RU' : 'en-GB';
  const confirm = useConfirm();
  const { user } = useAuth();
  const tz = user?.timezone || 'Europe/Moscow';
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [createdTags, setCreatedTags] = useState([]);
  const [bump, setBump] = useState(0);
  const [fuDate, setFuDate] = useState('');
  const [fuTime, setFuTime] = useState('09:00');
  const [fuNote, setFuNote] = useState('');
  const [sumTitle, setSumTitle] = useState('');
  const [sumText, setSumText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [velumaEnabled, setVelumaEnabled] = useState(null);
  const [draftCtx, setDraftCtx] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [completingActivityId, setCompletingActivityId] = useState(null);
  const closingRef = useRef(false);
  const reload = () => setBump((b) => b + 1);

  useEffect(() => {
    if (!contactId) return;
    let cancelled = false;
    api.get('/veluma/status')
      .then(({ data }) => { if (!cancelled) { setVelumaEnabled(!!data.enabled); setDraftResult(null); setDraftCtx(''); setCreatedTags([]); } })
      .catch(() => { if (!cancelled) setVelumaEnabled(false); });
    return () => { cancelled = true; };
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    closingRef.current = false;
    let cancelled = false;
    api.get(`/contacts/${contactId}`)
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        const c = d.contact;
        const initial = {
          name: c.name || '', company: c.company || '', role: c.role || '',
          email: c.email || '', phone: c.phone || '', telegram: c.telegram || '',
          host_note: c.host_note || '', tags: c.tags || [],
          vip: !!c.vip, remind_me: !!c.remind_me, remind_note: c.remind_note || '',
        };
        setForm(initial);
        setOriginalForm(initial);
        setEditing(false);
      })
      .catch(() => { if (!cancelled) toast.error(t('common.error')); });
    return () => { cancelled = true; };
  }, [contactId, t, bump]);

  const ready = data && form && data.contact?.id === contactId;
  const dirty = !!(form && originalForm) && JSON.stringify(form) !== JSON.stringify(originalForm);

  const requestClose = () => {
    if (closingRef.current) { onClose?.(); return; }
    if (dirty) setConfirmClose(true);
    else { closingRef.current = true; onClose?.(); }
  };

  const patch = async (body) => {
    const { data: updated } = await api.patch(`/contacts/${contactId}`, body);
    onPatched?.(updated);
    return updated;
  };

  const save = async () => {
    setSaving(true);
    try {
      await patch(form);
      setOriginalForm(form);
      toast.success(t('contacts.saved'));
      onClose?.();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const saveIdentity = async () => {
    setSaving(true);
    try {
      await patch(form);
      setOriginalForm(form);
      toast.success(t('contacts.saved'));
      setEditing(false);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const confirmSaveAndClose = async () => {
    setSaving(true);
    try {
      await patch(form);
      setOriginalForm(form);
      toast.success(t('contacts.saved'));
      closingRef.current = true;
      setConfirmClose(false);
      setTimeout(() => onClose?.(), 0);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };
  const discardAndClose = () => {
    closingRef.current = true;
    setConfirmClose(false);
    setTimeout(() => onClose?.(), 0);
  };

  const toggleTag = (tag) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((x) => x !== tag) : [...f.tags, tag],
    }));
  };

  const addTag = () => {
    const v = newTag.trim().toLowerCase();
    if (!v) return;
    if (!form.tags.includes(v)) setForm((f) => ({ ...f, tags: [...f.tags, v] }));
    setCreatedTags((c) => (c.includes(v) ? c : [...c, v]));
    setNewTag('');
  };

  const remove = async () => {
    try {
      const { data: guard } = await api.post('/contacts/delete-guard', { ids: [contactId] });
      if (guard?.has_upcoming) {
        await confirm({
          title: t('contacts.delete_blocked_title'),
          description: t('contacts.delete_blocked_upcoming'),
          alertOnly: true,
          destructive: true,
          confirmText: t('common.ok'),
        });
        return;
      }
    } catch {
      toast.error(t('common.error'));
      return;
    }
    if (!(await confirm({ description: t('contacts.delete_confirm'), confirmText: t('contacts.delete') }))) return;
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(t('contacts.deleted'));
      onDeleted?.(contactId);
      onClose?.();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail === 'has_upcoming') {
        await confirm({
          title: t('contacts.delete_blocked_title'),
          description: t('contacts.delete_blocked_upcoming'),
          alertOnly: true,
          destructive: true,
          confirmText: t('common.ok'),
        });
        return;
      }
      toast.error(t('common.error'));
    }
  };

  const promoteChannel = async (cid) => {
    try {
      const { data: updated } = await api.post(`/contacts/${contactId}/channels/${cid}/promote`);
      onPatched?.(updated);
      reload();
      toast.success(t('contacts.channel_promoted'));
    } catch { toast.error(t('common.error')); }
  };
  const deleteChannel = async (cid) => {
    try {
      const { data: updated } = await api.delete(`/contacts/${contactId}/channels/${cid}`);
      onPatched?.(updated);
      reload();
    } catch { toast.error(t('common.error')); }
  };

  const addFollowUp = async () => {
    if (!fuDate) return;
    try {
      const time = fuTime || '09:00';
      const dueAtUtc = wallTimeToUtc(`${fuDate}T${time}`, tz)?.toISOString() || null;
      await api.post(`/contacts/${contactId}/follow-ups`, {
        due_date: fuDate, due_time: time, due_at_utc: dueAtUtc, note: fuNote,
      });
      setFuDate(''); setFuTime('09:00'); setFuNote('');
      reload();
      toast.success(t('contacts.fu_added'));
    } catch { toast.error(t('common.error')); }
  };
  const toggleFollowUp = async (fu) => {
    try { await api.patch(`/contacts/${contactId}/follow-ups/${fu.id}`, { done: !fu.done }); reload(); }
    catch { toast.error(t('common.error')); }
  };
  const deleteFollowUp = async (fid) => {
    try { await api.delete(`/contacts/${contactId}/follow-ups/${fid}`); reload(); }
    catch { toast.error(t('common.error')); }
  };
  const completeActivityFollowUp = async (activity) => {
    setCompletingActivityId(activity.id);
    try {
      await api.post(`/contacts/${contactId}/follow-up-activities/${activity.id}/complete`);
      setData((current) => current ? {
        ...current,
        follow_up_activities: (current.follow_up_activities || []).filter((item) => item.id !== activity.id),
      } : current);
      onActivitiesChanged?.();
      toast.success(t('contacts.telegram_followup_done'));
    } catch { toast.error(t('common.error')); }
    finally { setCompletingActivityId(null); }
  };

  const addSummary = async () => {
    if (!sumTitle.trim() && !sumText.trim()) return;
    try {
      await api.post(`/contacts/${contactId}/summaries`, { title: sumTitle, text: sumText });
      setSumTitle(''); setSumText('');
      reload();
      toast.success(t('contacts.sum_added'));
    } catch { toast.error(t('common.error')); }
  };
  const uploadSummary = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', sumTitle || '');
      await api.post(`/contacts/${contactId}/summaries/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSumTitle('');
      reload();
      toast.success(t('contacts.sum_added'));
    } catch { toast.error(t('common.error')); }
    finally { setUploading(false); }
  };
  const deleteSummary = async (sid) => {
    try { await api.delete(`/contacts/${contactId}/summaries/${sid}`); reload(); }
    catch { toast.error(t('common.error')); }
  };

  const tagPool = Array.from(new Set([
    ...(suggestedTags || []), ...(allTags || []), ...createdTags, ...((form && form.tags) || []),
  ]));

  const draftInvite = async () => {
    setDrafting(true);
    setDraftResult(null);
    try {
      const { data: r } = await api.post('/veluma/draft-invite', { contact_id: contactId, context: draftCtx });
      if (!r.enabled) { setVelumaEnabled(false); toast.error(t('veluma.connect_hint')); return; }
      setDraftResult({ title: r.title, topic: r.topic });
    } catch { toast.error(t('common.error')); }
    finally { setDrafting(false); }
  };

  return (
    <>
    <Sheet open={!!contactId} onOpenChange={(o) => { if (!o) requestClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="contact-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {form?.vip && <Star className="h-5 w-5 fill-amber-400 text-amber-400" />}
            {t('contacts.detail_title')}
          </SheetTitle>
          <SheetDescription className="sr-only">{t('contacts.detail_title')}</SheetDescription>
        </SheetHeader>

        {!ready ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {/* Status toggles */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setForm((f) => ({ ...f, vip: !f.vip }))}
                data-testid="detail-vip-toggle"
                className={`flex items-center justify-center gap-2 rounded-lg border p-2.5 text-sm font-medium transition-colors ${form.vip ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                <Star className={`h-4 w-4 ${form.vip ? 'fill-amber-400 text-amber-400' : ''}`} /> {t('contacts.vip')}
              </button>
              <button
                onClick={() => setForm((f) => ({ ...f, remind_me: !f.remind_me }))}
                data-testid="detail-remind-toggle"
                className={`flex items-center justify-center gap-2 rounded-lg border p-2.5 text-sm font-medium transition-colors ${form.remind_me ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                <Bell className="h-4 w-4" /> {t('contacts.remind_me')}
              </button>
            </div>
            {form.remind_me && (
              <Input
                value={form.remind_note}
                placeholder={t('contacts.remind_note_ph')}
                onChange={(e) => setForm((f) => ({ ...f, remind_note: e.target.value }))}
                data-testid="detail-remind-note"
              />
            )}

            {/* Identity — read-only by default, pencil to edit (P2-01) */}
            {!editing ? (
              <div className="space-y-2" data-testid="detail-identity-view">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold truncate" data-testid="detail-view-name">{form.name || t('contacts.no_name')}</div>
                    {(form.company || form.role) && (
                      <div className="text-sm text-muted-foreground truncate" data-testid="detail-view-subtitle">
                        {[form.role, form.company].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 shrink-0 gap-1.5 text-muted-foreground" onClick={() => setEditing(true)} data-testid="detail-edit-toggle">
                    <Pencil className="h-4 w-4" /> {t('contacts.edit')}
                  </Button>
                </div>
                {(form.email || form.phone || form.telegram) && (
                  <div className="space-y-1 text-sm" data-testid="detail-view-channels">
                    {form.email && <div className="flex items-center gap-1.5 truncate" data-testid="detail-view-email"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{form.email}</div>}
                    {form.phone && <div className="flex items-center gap-1.5 truncate" data-testid="detail-view-phone"><Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{form.phone}</div>}
                    {form.telegram && <div className="flex items-center gap-1.5 truncate" data-testid="detail-view-telegram"><Send className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />@{form.telegram}</div>}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3" data-testid="detail-identity-edit">
                <div><Label>{t('contacts.col_name')}</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="detail-name" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{t('contacts.company')}</Label><Input className="mt-1" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="detail-company" /></div>
                  <div><Label className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{t('contacts.role')}</Label><Input className="mt-1" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="detail-role" /></div>
                </div>
                <div><Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />Email</Label><Input className="mt-1" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="detail-email" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{t('contacts.col_phone')}</Label><Input className="mt-1" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="detail-phone" /></div>
                  <div><Label className="flex items-center gap-1"><Send className="h-3.5 w-3.5" />Telegram</Label><Input className="mt-1" value={form.telegram} onChange={(e) => setForm((f) => ({ ...f, telegram: e.target.value }))} data-testid="detail-telegram" /></div>
                </div>
                <Button size="sm" onClick={saveIdentity} disabled={saving} data-testid="detail-edit-done">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" />{t('contacts.done')}</>}
                </Button>
              </div>
            )}

            {/* Quick actions — tap to call / email / message in one tap */}
            {(() => {
              const actions = [
                { type: 'phone', value: form.phone, Icon: Phone, label: t('contacts.col_phone') },
                { type: 'email', value: form.email, Icon: Mail, label: 'Email' },
                { type: 'telegram', value: form.telegram, Icon: Send, label: 'Telegram' },
              ].map((a) => ({ ...a, href: contactHref(a.type, a.value) })).filter((a) => a.href);
              if (actions.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-2" data-testid="detail-contact-actions">
                  {actions.map((a) => (
                    <a
                      key={a.type}
                      href={a.href}
                      {...contactLinkProps(a.href)}
                      data-testid={`detail-contact-action-${a.type}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                    >
                      <a.Icon className="h-4 w-4" /> {a.label}
                    </a>
                  ))}
                </div>
              );
            })()}

            {/* Secondary values entered by invitees (kept, not overwritten) */}
            {(data.contact.channels || []).length > 0 && (
              <div data-testid="detail-extra-channels">
                <Label className="flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5 text-primary" />{t('contacts.extra_values')}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{t('contacts.extra_values_hint')}</p>
                <ul className="mt-2 space-y-1.5">
                  {(data.contact.channels || []).map((ch) => {
                    const Icon = ch.type === 'email' ? Mail : ch.type === 'phone' ? Phone : Send;
                    return (
                      <li key={ch.id} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2 text-sm" data-testid={`detail-channel-${ch.id}`}>
                        <Icon className="h-4 w-4 shrink-0 text-amber-600" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{ch.value}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {ch.source === 'former_primary' ? t('contacts.channel_former') : t('contacts.channel_invitee')}
                            {ch.added_at ? ` · ${fmtDateTime(ch.added_at, locale)}` : ''}
                          </div>
                        </div>
                        <Button size="sm" variant="secondary" className="h-7 text-xs shrink-0" onClick={() => promoteChannel(ch.id)} data-testid={`detail-channel-promote-${ch.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> {t('contacts.channel_promote')}
                        </Button>
                        <button onClick={() => deleteChannel(ch.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`detail-channel-del-${ch.id}`}><X className="h-3.5 w-3.5" /></button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Tags */}
            <div>
              <Label>{t('contacts.tags')}</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="detail-tags">
                {tagPool.map((tag) => {
                  const on = form.tags.includes(tag);
                  return (
                    <button key={tag} onClick={() => toggleTag(tag)} data-testid={`detail-tag-${tag}`}
                      className={`rounded-full border px-2.5 py-0.5 text-xs transition-all ${on ? tagColor(tag) : 'border-dashed border-border text-muted-foreground hover:bg-muted'}`}>
                      {t(`contacts.tag_names.${tag}`, tag)}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-2">
                <Input className="h-8 text-sm" value={newTag} placeholder={t('contacts.add_tag_ph')}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  data-testid="detail-new-tag" />
                <Button type="button" variant="secondary" size="sm" onClick={addTag} data-testid="detail-add-tag"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label>{t('contacts.note')}</Label>
              <Textarea className="mt-1" rows={3} value={form.host_note}
                placeholder={t('contacts.note_ph')}
                onChange={(e) => setForm((f) => ({ ...f, host_note: e.target.value }))}
                data-testid="detail-note" />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} className="flex-1" data-testid="detail-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('contacts.save')}
              </Button>
              <Button variant="secondary" onClick={() => onPlan?.(data.contact)} data-testid="detail-plan">
                <CalendarClock className="h-4 w-4 mr-1.5" /> {t('contacts.plan_meeting')}
              </Button>
            </div>

            {/* Veluma AI draft (KAL-34) — temporarily hidden */}
            {false && (
            <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-3" data-testid="detail-veluma">
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-primary" /> {t('veluma.draft_title')}
              </div>
              <Input className="mt-2 h-9 text-sm" placeholder={t('veluma.draft_context_ph')} value={draftCtx}
                onChange={(e) => setDraftCtx(e.target.value)} data-testid="veluma-draft-context" />
              <Button size="sm" className="mt-2 w-full" onClick={draftInvite} disabled={drafting} data-testid="veluma-draft-btn">
                {drafting ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />{t('veluma.thinking')}</> : <><Sparkles className="h-4 w-4 mr-1.5" />{t('veluma.draft_btn')}</>}
              </Button>
              {velumaEnabled === false && (
                <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="veluma-draft-locked">
                  <Lock className="h-3 w-3" /> {t('veluma.connect_hint')}
                </p>
              )}
              {draftResult && (
                <div className="mt-2 rounded-lg border border-border bg-card p-2.5 text-sm" data-testid="veluma-draft-result">
                  <div className="font-medium">{draftResult.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{draftResult.topic}</div>
                  <Button size="sm" variant="secondary" className="mt-2 h-7 text-xs" onClick={() => onPlan?.(data.contact, draftResult)} data-testid="veluma-draft-use">
                    <CalendarClock className="h-3.5 w-3.5 mr-1" /> {t('veluma.use_draft')}
                  </Button>
                </div>
              )}
            </div>
            )}

            {/* Follow-ups (KAL-32) */}
            <div data-testid="detail-followups">
              <Label className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5 text-primary" />{t('contacts.followups')}</Label>
              <div className="mt-1.5 space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1"><DateField size="md" min={minDateStr()} value={fuDate} onChange={setFuDate} testId="detail-fu-date" /></div>
                  <div className="w-32"><TimeField size="md" value={fuTime} onChange={setFuTime} testId="detail-fu-time" /></div>
                </div>
                <div className="flex gap-2">
                  <Input className="h-9 flex-1" placeholder={t('contacts.fu_note_ph')} value={fuNote} onChange={(e) => setFuNote(e.target.value)} data-testid="detail-fu-note" />
                  <Button size="sm" className="h-9 shrink-0" onClick={addFollowUp} disabled={!fuDate} data-testid="detail-fu-add"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              {(data.contact.follow_ups || []).length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="detail-fu-list">
                  {(data.contact.follow_ups || []).map((fu) => (
                    <li key={fu.id} className={`flex items-center gap-2 rounded-lg border p-2 text-sm ${fu.done ? 'border-border opacity-60' : 'border-primary/30 bg-primary/[0.04]'}`} data-testid={`detail-fu-${fu.id}`}>
                      <button onClick={() => toggleFollowUp(fu)} data-testid={`detail-fu-toggle-${fu.id}`} className="shrink-0">
                        {fu.done ? <Check className="h-4 w-4 text-emerald-600" /> : <span className="block h-4 w-4 rounded border border-muted-foreground/40" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className={`truncate ${fu.done ? 'line-through' : ''}`}>{fu.note || t('contacts.fu_default')}</div>
                        <div className="text-xs text-muted-foreground">{fmtFollowUp(fu, tz, locale)}</div>
                      </div>
                      <button onClick={() => deleteFollowUp(fu.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`detail-fu-del-${fu.id}`}><X className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {(data.follow_up_activities || []).length > 0 && (
                <div className="mt-3" data-testid="detail-activity-followups">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Bell className="h-3.5 w-3.5 text-primary" /> {t('contacts.telegram_followups')}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t('contacts.telegram_followups_hint')}</p>
                  <ul className="mt-2 space-y-1.5">
                    {(data.follow_up_activities || []).map((activity) => (
                      <li key={activity.id} className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/[0.04] p-2 text-sm" data-testid={`detail-activity-followup-${activity.id}`}>
                        <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{activity.title || activity.note || t('contacts.fu_default')}</div>
                          {activity.title && activity.note && activity.note !== activity.title && (
                            <div className="truncate text-xs text-muted-foreground">{activity.note}</div>
                          )}
                          <div className="text-xs text-muted-foreground">{fmtActivityDateTime(activity, locale)}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 shrink-0 px-2 text-xs"
                          onClick={() => completeActivityFollowUp(activity)}
                          disabled={completingActivityId === activity.id}
                          data-testid={`detail-activity-followup-done-${activity.id}`}
                        >
                          {completingActivityId === activity.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                          {t('contacts.done')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Meeting summaries (KAL-33) */}
            <div data-testid="detail-summaries">
              <Label className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-primary" />{t('contacts.summaries')}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{t('contacts.summaries_hint')}</p>
              <Input className="mt-1.5 h-9" placeholder={t('contacts.sum_title_ph')} value={sumTitle} onChange={(e) => setSumTitle(e.target.value)} data-testid="detail-sum-title" />
              <Textarea className="mt-1.5" rows={2} placeholder={t('contacts.sum_text_ph')} value={sumText} onChange={(e) => setSumText(e.target.value)} data-testid="detail-sum-text" />
              <div className="mt-1.5 flex gap-2">
                <Button size="sm" onClick={addSummary} disabled={!sumTitle.trim() && !sumText.trim()} data-testid="detail-sum-add">{t('contacts.sum_save')}</Button>
                <label className="shrink-0">
                  <input type="file" className="hidden" onChange={uploadSummary} accept=".pdf,.doc,.docx,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.rtf,.txt,.md,.csv" data-testid="detail-sum-file" />
                  <Button type="button" size="sm" variant="secondary" asChild disabled={uploading}>
                    <span>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}<span className="ml-1.5">{t('contacts.sum_upload')}</span></span>
                  </Button>
                </label>
              </div>
              {(data.contact.summaries || []).length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="detail-sum-list">
                  {(data.contact.summaries || []).map((s) => (
                    <li key={s.id} className="flex items-start gap-2 rounded-lg border border-border p-2 text-sm" data-testid={`detail-sum-${s.id}`}>
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{s.title || t('contacts.sum_untitled')}</div>
                        {s.text && <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{s.text}</div>}
                        {s.url && <a href={classifyLink(s.url).href} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{s.file_name}</a>}
                        <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(s.created_at, locale)}</div>
                      </div>
                      <button onClick={() => deleteSummary(s.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`detail-sum-del-${s.id}`}><X className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Timeline */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>{t('contacts.timeline')}</Label>
                <span className="text-xs text-muted-foreground" data-testid="detail-meet-count">
                  {t('contacts.last_met')}: {fmtDateTime(data.contact.last_met, locale)}
                </span>
              </div>
              {data.timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="detail-timeline-empty">{t('contacts.no_interactions')}</p>
              ) : (
                <ul className="space-y-2" data-testid="detail-timeline">
                  {data.timeline.map((it, i) => {
                    const Icon = TYPE_ICON[it.type] || CalendarClock;
                    return (
                      <li key={i} className="flex items-start gap-2.5 rounded-lg border border-border p-2.5 text-sm">
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{it.title || t(`contacts.itype.${it.type}`, it.type)}</div>
                          <div className="text-xs text-muted-foreground">
                            {fmtDateTime(it.when, locale)}
                            {it.status ? ` · ${it.status}` : ''}
                          </div>
                        </div>
                        <Badge variant="secondary" className="ml-auto shrink-0 text-[10px]">{t(`contacts.itype.${it.type}`, it.type)}</Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <button onClick={remove} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive" data-testid="detail-delete">
              <Trash2 className="h-3.5 w-3.5" /> {t('contacts.delete')}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>

      <AlertDialog open={confirmClose} onOpenChange={(o) => { if (!o) setConfirmClose(false); }}>
        <AlertDialogContent className="rounded-2xl max-w-md" data-testid="contact-unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="contact-unsaved-title">{t('contacts.unsaved_title')}</AlertDialogTitle>
            <AlertDialogDescription data-testid="contact-unsaved-desc">{t('contacts.unsaved_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={discardAndClose} className="rounded-xl" data-testid="contact-unsaved-discard">
              {t('contacts.dont_save')}
            </Button>
            <Button onClick={confirmSaveAndClose} disabled={saving} className="rounded-xl" data-testid="contact-unsaved-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('contacts.save')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
